import React, { useState, useCallback } from 'react'
import { StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { useNodeTopContributionsQuery } from '../../../../generated/graphql'
import { ContributionStatRow } from '../components/contribution-stat.row'
import {
  ExportButton,
  ExportBanner,
} from '../../../../@ui/buttons/export.button'
import { SortOption, SortOptions } from '../../../../@ui/buttons/sort.button'
import { useExportContributions } from '../hooks/export-node-contributions.hook'
import Row from '../../../../@ui/containers/grid/row'
import PaginatedFlatList from '../../../../@ui/containers/lists/paginated-flat.list'

interface ContributionStatsTabProps {
  nodeId: string
  nodeName: string
}

const sortOptions = (
  t: TFunction<'translation', undefined>
): SortOption<string>[] => [
  {
    field: 'bumpScore',
    label: t('sort-most-bumps'),
    direction: 'DESC',
    icon: 'sort-numeric-descending',
  },
  {
    field: 'bumpScore',
    label: t('sort-least-bumps'),
    direction: 'ASC',
    icon: 'sort-numeric-ascending',
  },
  {
    field: 'date',
    label: t('sort-newest-first'),
    direction: 'DESC',
    icon: 'sort-calendar-descending',
  },
  {
    field: 'date',
    label: t('sort-oldest-first'),
    direction: 'ASC',
    icon: 'sort-calendar-ascending',
  },
]

export function ContributionStatsTab({
  nodeId,
  nodeName,
}: ContributionStatsTabProps) {
  const { t } = useTranslation('nodes')
  const [limit] = useState(30)
  const [sortField, setSortField] = useState<'bumpScore' | 'date'>('bumpScore')
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC')

  const { exportContributions, isExporting, progress } = useExportContributions(
    {
      nodeId,
      nodeName,
      sortBy: sortField,
      sort: sortDirection,
    }
  )

  const { data, loading, refetch, error, fetchMore } =
    useNodeTopContributionsQuery({
      variables: {
        input: {
          nodeId,
          limit,
          offset: 0,
          sortBy: sortField,
          sort: sortDirection,
        },
      },
    })

  const contributions = data?.nodeTopContributions?.contributions || []
  const total = data?.nodeTopContributions?.total || 0

  // Debug logging
  if (error) {
    console.error('Contribution stats query error:', error)
  }
  if (data) {
    console.log('Contribution stats data:', {
      total,
      contributionsCount: contributions.length,
    })
  }

  const handleSortChange = useCallback(
    (field: string, direction: 'ASC' | 'DESC') => {
      setSortField(field as 'bumpScore' | 'date')
      setSortDirection(direction)
    },
    []
  )

  const handleLoadMore = useCallback(
    async (params: { offset: number; limit: number }) => {
      if (loading) return

      try {
        await fetchMore({
          variables: {
            input: {
              nodeId,
              limit: params.limit,
              offset: params.offset,
              sortBy: sortField,
              sort: sortDirection,
            },
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev
            return {
              nodeTopContributions: {
                ...fetchMoreResult.nodeTopContributions,
                contributions: [
                  ...prev.nodeTopContributions.contributions,
                  ...fetchMoreResult.nodeTopContributions.contributions,
                ],
              },
            }
          },
        })
      } catch (error) {
        console.error('Error loading more contributions:', error)
      }
    },
    [nodeId, loading, fetchMore, sortField, sortDirection]
  )

  const handleRefresh = useCallback(async () => {
    try {
      await refetch({
        input: {
          nodeId,
          limit,
          offset: 0,
          sortBy: sortField,
          sort: sortDirection,
        },
      })
    } catch (error) {
      console.error('Error refreshing contributions:', error)
    }
  }, [refetch, nodeId, limit, sortField, sortDirection])

  return (
    <View style={styles.container}>
      <ExportBanner
        visible={isExporting}
        progress={progress}
        message={t('exporting-contributions')}
      />

      <View style={styles.header}>
        <Row justifyContent="space-between" alignItems="center">
          <Text variant="titleMedium">
            {t('contribution-stats')} ({total})
          </Text>
          <Row gap={1}>
            <ExportButton
              onPress={exportContributions}
              disabled={isExporting || !contributions.length}
              tooltip={t('export-contributions-to-csv')}
            />
            <SortOptions
              selectedField={sortField}
              selectedDirection={sortDirection}
              onSortChange={handleSortChange}
              options={sortOptions(t)}
            />
          </Row>
        </Row>
      </View>

      <PaginatedFlatList
        data={contributions}
        total={total}
        initialLimit={limit}
        style={styles.listContainer}
        contentContainerStyle={styles.listContent}
        renderItem={({ item, index }) => {
          const contribution = item as NonNullable<(typeof contributions)[0]>
          return (
            <ContributionStatRow contribution={contribution} rank={index + 1} />
          )
        }}
        keyExtractor={(item) => {
          const contribution = item as NonNullable<(typeof contributions)[0]>
          return contribution.id
        }}
        onLoadMore={handleLoadMore}
        onRefresh={handleRefresh}
        emptyMessage={t('no-contributions-found')}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
})
